import momentTimezone from 'moment-timezone';
import React, {memo, useContext, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {ThemeContext} from '../../apiContexts';
import LoadingBar from '../../components/loadingBar';
import ReactTable from '../../components/reactTable';
import {
    convertDistanceTo,
    convertVolumeTo,
    nomenclatureDistance,
    nomenclatureVolume,
} from '../../helpers/conversions';
import {durationFormat} from '../../helpers/date';
import {getColumnWidth} from '../../helpers/report';
import ReplayControls from './replay-controls';
import {Header, ListStyle} from './styles';
import SelectDeviceButton from './selectDeviceButton';

const JourneyList = ({
    data,
    timezone,
    onSelect,
    loading,
    selectingRoute,
    distanceUnit,
    speedUnit,
    volumeUnit,
}) => {
    const theme = useContext(ThemeContext);
    const intl = useIntl();

    const formatType = (type) =>
        type === 'trip' ?
            intl.formatMessage({id: 'journeyTypeMovement'}) :
            intl.formatMessage({id: 'journeyTypeStopped'});

    const formatTime = (value) =>
        value ? momentTimezone(value).tz(timezone).format(intl.formatMessage({id: 'dateTimeLongFormat'})) : '-';

    const columns = useMemo(
        () => [
            {
                Header: intl.formatMessage({id: 'sharedName'}),
                accessor: 'deviceName',
                minWidth: 200,
                width: getColumnWidth(data, 'deviceName', intl.formatMessage({id: 'sharedName'})),
                Cell: ({row: {original}}) =>
                    original.groupName ? `${original.deviceName} — ${original.groupName}` : original.deviceName,
            },
            {
                Header: intl.formatMessage({id: 'journeyColStatus'}),
                accessor: 'type',
                Cell: ({cell: {value}}) => formatType(value),
            },
            {
                Header: intl.formatMessage({id: 'journeyColStart'}),
                accessor: 'startTime',
                Cell: ({cell: {value}}) => formatTime(value),
                minWidth: 200,
            },
            {
                Header: intl.formatMessage({id: 'journeyColEnd'}),
                accessor: 'endTime',
                Cell: ({cell: {value}}) => formatTime(value),
                minWidth: 200,
            },
            {
                Header: intl.formatMessage({id: 'journeyColDistance'}, {unit: nomenclatureDistance(distanceUnit)}),
                accessor: 'distance',
                Cell: ({cell: {value}}) => `${Number(convertDistanceTo(value, distanceUnit)).toFixed(2) || 0}`,
            },
            {
                Header: intl.formatMessage({id: 'journeyColDuration'}),
                accessor: 'duration',
                Cell: ({cell: {value}}) => durationFormat(value, 'H [h] mm [m]'),
            },
            {
                Header: intl.formatMessage({id: 'journeyColEngine'}),
                accessor: 'engineHours',
                Cell: ({cell: {value}}) => durationFormat(value, 'H [h] mm [m]'),
            },
            {
                Header: intl.formatMessage({id: 'journeyColFuel'}, {unit: nomenclatureVolume(volumeUnit)}),
                accessor: 'spentFuel',
                Cell: ({cell: {value}}) => `${Number(convertVolumeTo(value, volumeUnit)).toFixed(1) || 0}`,
            },
            {
                Header: intl.formatMessage({id: 'journeyColDriver'}),
                accessor: 'driverName',
                Cell: ({row: {original}}) => original.driverName || original.driverUniqueId || '-',
            },
            {
                Header: intl.formatMessage({id: 'journeyColEvents'}),
                accessor: 'eventsSummary',
                Cell: ({cell: {value}}) => value || '-',
            },
            {
                Header: intl.formatMessage({id: 'journeyColStartLoc'}),
                accessor: 'startAddress',
                Cell: ({cell: {value}}) => value || '-',
                minWidth: 150,
                width: getColumnWidth(data, 'startAddress', intl.formatMessage({id: 'journeyColStartLoc'})),
            },
            {
                Header: intl.formatMessage({id: 'journeyColEndLoc'}),
                accessor: 'endAddress',
                Cell: ({cell: {value}}) => value || '-',
                minWidth: 150,
                width: getColumnWidth(data, 'endAddress', intl.formatMessage({id: 'journeyColEndLoc'})),
            },
        ],
        [data, distanceUnit, timezone, volumeUnit],
    );

    const selectItem = (props) => onSelect(props);

    return (
        <ListStyle theme={theme} className="custom-shadow-2">
            <Header>
                <h5>{intl.formatMessage({id: 'journeyHistory'})}</h5>
                {selectingRoute && (
                    <small className="text-muted">
                        {intl.formatMessage({id: 'journeyLoadingRoute'})}
                    </small>
                )}
                <ReplayControls />
                <SelectDeviceButton />
            </Header>
            {loading ? (
                <LoadingBar />
            ) : (
                <ReactTable data={data} columns={columns} selectItem={selectItem} />
            )}
        </ListStyle>
    );
};

export default memo(JourneyList);
